import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageSquare, Calendar, UserCheck, Clock,
  AlertTriangle, Frown, Zap, ChevronRight, CheckCircle2,
  HelpCircle, ChevronDown, Layers, CalendarSync,
  Link2, Wallet, Megaphone, Users, ShoppingBag, Star, Building2
} from 'lucide-react';

import { LuInstagram, LuFacebook, LuMessageCircle } from 'react-icons/lu';
import { PRECOS, PLANOS_VENDAVEIS } from '../config/planos';

const fmt = (valor) => valor.toFixed(2).replace('.', ',');

// Copy de vendas por plano — os números vêm de PRECOS (mesma fonte usada no painel logado em
// PlanosPage.js), só o texto dos bullets é específico da landing (tom de conversão).
const BULLETS_LANDING = {
  COMUM: [
    'Agendamentos e clientes ilimitados',
    'Agenda pública personalizada (link próprio)',
    'Controle Financeiro (receitas e recebimentos)',
    'Diferenciais e cores da página pública',
    '1 Google Agenda vinculada',
  ],
  PREMIUM: [
    'Tudo do Plano Comum, e mais:',
    'Visão Geral completa do negócio',
    'Marketing automático: Cupons, Fidelidade e Aniversário',
    'Lembretes individuais e em massa via WhatsApp',
    'Robô de WhatsApp conectado (1 número)',
  ],
  EMPRESARIAL: [
    'Tudo do Plano Premium, e mais:',
    'Gestão de Equipe (cadastre profissionais)',
    'Até 5 números de WhatsApp conectados',
    'Múltiplas Agendas Google (uma por profissional)',
  ],
};

const LandingPage = () => {
  const [faqAberto, setFaqAberto] = useState(null);

  const faqs = [
    {
      pergunta: "O robô atende em grupos?",
      resposta: "Não. O assistente funciona apenas em conversas individuais para garantir a privacidade dos seus clientes e manter a organização dos agendamentos."
    },
    {
      pergunta: "Posso usar o WhatsApp normalmente enquanto o robô funciona?",
      resposta: "Sim! Você pode responder manualmente quando quiser. O robô detecta sua interferência e aguarda o tempo de 'Reset Bot' configurado para atuar novamente, sem atropelar o seu atendimento humano."
    },
    {
      pergunta: "Preciso baixar algum aplicativo no meu celular?",
      resposta: "Não! A Agenda Sincro funciona 100% na nuvem. Você só precisa ler o QR Code ou o Código de pareamento no nosso painel (como se fosse conectar o WhatsApp Web) e o sistema já começa a trabalhar."
    },
    {
      pergunta: "Como funciona a sincronização com o Google Agenda?",
      resposta: "Conecte seu Google Agenda e deixe o robô trabalhar. Ele consulta seus horários livres em tempo real e adiciona novos agendamentos automaticamente, respeitando totalmente a sua agenda atual e sem modificar seus compromissos anteriores."
    },
    {
      pergunta: "Posso cancelar o teste gratuito a qualquer momento?",
      resposta: "Com certeza. Não há fidelidade ou taxas ocultas. Você pode testar todas as funcionalidades grátis por 7 dias e cancelar com um clique antes da cobrança."
    },
    {
      pergunta: "Eu perco o controle da minha agenda?",
      resposta: "De forma alguma. O Agenda Sincro apenas organiza e preenche as vagas. Você tem total controle sobre sua agenda no Google e pode bloquear horários ou intervir na conversa do robô a qualquer momento."
    },
    {
      pergunta: "Os clientes acham que é um robô?",
      resposta: "O fluxo é tão natural que parece um atendimento pessoal. Como o sistema apenas informa horários e confirma agendamentos, o cliente sente que está falando com um assistente eficiente, não com um bot."
    }
  ];

  const toggleFaq = (index) => {
    setFaqAberto(faqAberto === index ? null : index);
  };

  const [ciclo, setCiclo] = useState('MENSAL');

  return (
    <div className="min-h-screen bg-[#F8F7FF] font-sans text-[#0F172A] overflow-x-hidden">
      
      {/* 1. HEADER */}
      <header className="sticky top-0 z-50 flex h-20 items-center justify-between px-6 md:px-12 bg-white/80 backdrop-blur-md border-b border-purple-100">
        <div className="flex items-center">
          <Link to="/" className="flex items-center no-underline">
            <img 
              src="/logo-sincro2.png" 
              alt="Agenda Sincro Logo" 
              className="h-11 w-auto object-contain transition-transform duration-300 ease-out hover:scale-[1.02]"
            />
          </Link>
        </div>

        <div className="flex items-center gap-4">
          <Link to="/login" className="no-underline text-[#0F172A] px-4 py-2 rounded-lg font-semibold text-sm transition-colors hover:text-[#6D28D9]">
            Entrar
          </Link>
          <Link to="/register" className="no-underline text-white bg-gradient-to-r from-[#1883FF] to-[#6D28D9] px-5 py-2.5 rounded-xl font-bold text-sm shadow-[0_10px_20px_rgba(109,40,217,0.2)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_15px_30px_rgba(109,40,217,0.3)]">
            Começar Agora
          </Link>
        </div>
      </header>

      {/* 2. HERO SECTION LADO A LADO */}
      <section className="relative px-6 py-20 md:py-32 bg-[radial-gradient(120%_120%_at_50%_0%,#E0E7FF_0%,#F8F7FF_100%)]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
          
          {/* Coluna de Texto */}
          <div className="flex-1 text-left">
            <div className="inline-flex items-center bg-white text-[#6D28D9] px-5 py-2 rounded-full text-sm font-bold mb-6 border border-purple-100 shadow-lg shadow-purple-500/5">
              <Zap size={14} className="mr-1.5 animate-bounce" /> Nova Geração de Automação
            </div>
            
            <h1 className="text-4xl md:text-6xl font-extrabold text-[#0F172A] leading-[1.1] tracking-tight mb-6">
              Agendamentos 24 horas por dia com um <br/>
              <span className="bg-gradient-to-r from-[#1883FF] to-[#6D28D9] bg-clip-text text-transparent">
                WhatsApp Inteligente
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-[#64748B] mb-8 leading-relaxed">
              Automatize agendamentos, integre com o Google Agenda e tenha uma secretária digital trabalhando 24h por dia. O fim das mensagens acumuladas.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Link to="/register" className="no-underline text-white bg-gradient-to-r from-[#1883FF] to-[#6D28D9] font-bold px-8 py-4 rounded-xl shadow-[0_10px_20px_rgba(109,40,217,0.3)] hover:-translate-y-0.5 transition-all">
                Quero Automatizar
              </Link>
              <p className="text-sm text-[#64748B] font-medium">✓ Teste grátis de 7 dias</p>
            </div>
          </div>

          {/* Coluna da Imagem (Mascote) */}
          <div className="flex-1 flex justify-center md:justify-end">
            <img 
              src="/2.png" 
              alt="Assistente Agenda Sincro" 
              className="w-full max-w-sm h-auto object-contain drop-shadow-2xl animate-fade-in-up" 
            />
          </div>
        </div>
      </section>

      {/* 3. A DOR */}
      <section className="px-6 py-20 md:py-28 bg-white">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight text-center mb-12">
          O agendamento manual está roubando o seu tempo
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-red-50 border border-red-100">
              <Frown size={24} className="text-red-500" />
            </div>
            <h4 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Clientes sumidos e esquecidos</h4>
            <p className="text-sm leading-relaxed text-[#64748B]">Sem um sistema de lembretes, você perde o contato com clientes que já foram atendidos e estão há dias sem marcar o retorno.</p>
          </div>

          <div className="bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-amber-50 border border-amber-100">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <h4 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Faltas e Esquecimentos</h4>
            <p className="text-sm leading-relaxed text-[#64748B]">Controlar manualmente a agenda e esquecer de enviar o lembrete um dia antes gera buracos na agenda e frustração.</p>
          </div>

          <div className="bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-red-50 border border-red-100">
              <Clock size={24} className="text-red-500" />
            </div>
            <h4 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Trabalho Repetitivo</h4>
            <p className="text-sm leading-relaxed text-[#64748B]">Gastar horas do seu dia apenas para confirmar horários, enviar avisos e digitar mensagens repetitivas não é um modelo eficiente.</p>
          </div>
        </div>
      </section>

      {/* 4. A SOLUÇÃO */}
      <section className="px-6 py-20 md:py-28 bg-[#F8F7FF]">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          
          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Zap size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Configuração Express</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Sem setups complexos. Conecte seu WhatsApp e integre seu calendário de atendimentos em instantes com nossa interface guiada.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <MessageSquare size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Encerramento Organizado</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Assim que o fluxo é concluído, o robô envia automaticamente uma mensagem notificando o encerramento do atendimento, mantendo a conversa limpa e profissional.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <UserCheck size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Painel de Controle Híbrido</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Realize novos agendamentos manualmente direto pela plataforma e configure o envio imediato ou programado de lembretes direto para o WhatsApp do cliente.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Clock size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Lembretes Avulsos Rápidos</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Dispare avisos avulsos customizados separando o horário real do atendimento na agenda do horário exato do envio da notificação no WhatsApp.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Layers size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Pacotes e Múltiplas Sessões</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Agende lembretes estruturados em blocos de pacotes para o acompanhamento completo de todas as sessões do cliente sem precisar criar um por um.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <CalendarSync size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Sincronismo com Google Agenda</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Integração direta. O sistema inteligente lê e bloqueia horários ocupados de atendimentos automaticamente para evitar conflitos de horários na sua semana.</p>
          </div>

        </div>
      </section>

      {/* 4.5 MUITO ALÉM DO WHATSAPP */}
      <section className="px-6 py-20 md:py-28 bg-white">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight mb-6">
            Muito além do <span className="text-[#6D28D9]">WhatsApp</span>
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Uma plataforma completa pra rodar o seu negócio: da vitrine pública que capta o cliente até o controle financeiro de cada atendimento.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Link2 size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Agenda Pública Própria</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Um link de agendamento personalizado com sua marca, endereço e diferenciais, pra clientes marcarem o próprio horário sem precisar chamar no WhatsApp.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <ShoppingBag size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Catálogo Multi-Serviço</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Cadastre todos os seus serviços com preço e duração, e deixe o cliente escolher um ou vários numa mesma marcação — o robô calcula o horário certo pra tudo.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Wallet size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Controle Financeiro</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Acompanhe a receita de cada atendimento concluído, filtre por período e enxergue exatamente quanto seu negócio faturou, sem precisar de planilha.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Megaphone size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Marketing Automático</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Cupons de desconto, programa de fidelidade que premia clientes recorrentes e mensagem de aniversário automática — tudo disparado sozinho pelo WhatsApp.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <UserCheck size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Gestão de Clientes</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Um cadastro completo de cada cliente com telefone, aniversário e histórico de atendimentos, pra você nunca mais perder o contato de quem já confiou no seu trabalho.</p>
          </div>

          <div className="group bg-white p-10 rounded-[28px] border border-slate-200/60 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.03)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_30px_60px_-15px_rgba(15,23,42,0.08)] hover:border-[#6D28D9]/25">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-purple-50 transition-colors duration-300 group-hover:bg-gradient-to-r from-[#1883FF] to-[#6D28D9]">
              <Users size={24} className="text-[#6D28D9] transition-all duration-300 group-hover:text-white group-hover:scale-105" />
            </div>
            <h3 className="text-xl font-bold text-[#0F172A] mb-3 tracking-tight">Gestão de Equipe</h3>
            <p className="text-sm leading-relaxed text-[#64748B]">Cadastre os profissionais do seu negócio, vincule a Google Agenda de cada um e conecte números de WhatsApp extras pra recepção, vendas ou atendentes.</p>
          </div>

        </div>
      </section>

      {/* 5. PRECIFICAÇÃO */}
      <section className="px-6 py-20 md:py-28 bg-white" id="planos">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight mb-6">
            Escolha o plano <span className="text-[#6D28D9]">ideal para você</span>
          </h2>
          <p className="text-lg text-[#64748B] mb-12 max-w-2xl mx-auto">
            Tenha acesso a todas as ferramentas que você precisa para automatizar sua agenda, com opções que acompanham o crescimento do seu negócio.
          </p>

          {/* 🔥 SWITCH MENSAL / ANUAL */}
          <div className="flex justify-center mb-16">
            <div className="bg-slate-100 p-1.5 rounded-full flex items-center gap-1 relative shadow-inner">
              <button
                onClick={() => setCiclo('MENSAL')}
                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 ${ciclo === 'MENSAL' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Mensal
              </button>
              <button
                onClick={() => setCiclo('ANUAL')}
                className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${ciclo === 'ANUAL' ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Anual <span className="bg-emerald-400 text-emerald-950 text-[10px] px-2 py-0.5 rounded-full uppercase font-black tracking-wider">Desconto</span>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1200px] mx-auto">
            {PLANOS_VENDAVEIS.map((plano) => {
              const preco = PRECOS[plano.id];
              const destaque = plano.id === 'PREMIUM';
              return (
                <div
                  key={plano.id}
                  className={`bg-white rounded-[32px] p-8 md:p-10 flex flex-col transition-all relative text-left ${
                    destaque
                      ? 'border-2 border-[#6D28D9] shadow-[0_20px_50px_-12px_rgba(109,40,217,0.15)] hover:-translate-y-1'
                      : 'border-2 border-slate-200 shadow-sm hover:border-[#1883FF]/50 hover:shadow-lg'
                  }`}
                >
                  {destaque && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#1883FF] to-[#6D28D9] text-white px-6 py-2 rounded-full text-[11px] font-black uppercase tracking-widest shadow-lg whitespace-nowrap flex items-center gap-1.5">
                      <Star size={12} fill="white" /> {plano.badge}
                    </div>
                  )}

                  <div className={`mb-8 ${destaque ? 'mt-2' : ''}`}>
                    <h3 className={`text-2xl font-black uppercase tracking-tight mb-2 flex items-center gap-2 ${destaque ? 'text-[#6D28D9]' : 'text-slate-900'}`}>
                      {plano.nome} {plano.id === 'EMPRESARIAL' && <Building2 size={22} />}
                    </h3>
                    <p className="text-sm font-medium text-slate-500 h-10">{plano.descricao}</p>
                    <div className="flex items-baseline gap-1 mt-6">
                      <span className="text-2xl font-bold text-[#64748B]">R$</span>
                      <span className="text-6xl font-black text-[#0F172A] tracking-tighter">
                        {fmt(ciclo === 'ANUAL' ? preco.anualMes : preco.mensal)}
                      </span>
                      <span className="text-lg font-medium text-[#64748B]">/mês</span>
                    </div>
                    <p className={`text-[11px] font-bold mt-2 h-4 ${ciclo === 'ANUAL' ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {ciclo === 'ANUAL' ? `Economize R$ ${preco.economia}! Faturado R$ ${fmt(preco.anualTotal)}/ano` : 'Faturado mensalmente'}
                    </p>
                  </div>

                  <div className={`flex flex-col gap-4 mb-10 flex-1 p-6 rounded-3xl border ${destaque ? 'bg-purple-50/50 border-purple-100' : 'bg-slate-50 border-slate-100'}`}>
                    {BULLETS_LANDING[plano.id].map((bullet, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <CheckCircle2 size={20} className={`${destaque ? 'text-[#6D28D9]' : 'text-[#1883FF]'} shrink-0 mt-0.5`} strokeWidth={3} />
                        <span className="text-[#0F172A] font-semibold text-sm">{bullet}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/register"
                    className={`no-underline flex items-center justify-center gap-2 w-full text-white py-4 rounded-2xl text-sm font-black uppercase tracking-wider transition-all hover:-translate-y-1 shadow-md ${
                      destaque
                        ? 'bg-gradient-to-r from-[#1883FF] to-[#6D28D9] hover:opacity-90 shadow-lg shadow-[#6D28D9]/20'
                        : 'bg-slate-900 hover:bg-[#1883FF]'
                    }`}
                  >
                    Começar Teste Grátis <ChevronRight size={18} />
                  </Link>
                </div>
              );
            })}
          </div>
          
          <p className="mt-10 text-xs text-[#64748B] font-bold text-center uppercase tracking-wide">
            Teste grátis por 7 dias. Cancele a qualquer momento.
          </p>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="px-6 py-20 md:py-28 bg-[#FAFAFA]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center w-14 h-14 bg-purple-50 text-[#6D28D9] rounded-2xl mx-auto mb-6">
              <HelpCircle size={28} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0F172A] tracking-tight mb-4">
              Dúvidas Frequentes
            </h2>
            <p className="text-lg text-[#64748B]">Tudo o que você precisa saber antes de começar a automatizar sua agenda.</p>
          </div>

          <div className="flex flex-col gap-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className={`bg-white rounded-2xl border transition-all duration-300 ${faqAberto === index ? 'border-[#6D28D9] shadow-md' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <button 
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-6 text-left cursor-pointer focus:outline-none"
                >
                  <span className="text-base sm:text-lg font-bold text-[#0F172A] pr-4">{faq.pergunta}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 shrink-0 ${faqAberto === index ? 'bg-purple-50 text-[#6D28D9] rotate-180' : 'bg-slate-50 text-slate-400'}`}>
                    <ChevronDown size={20} />
                  </div>
                </button>
                
                <div 
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${faqAberto === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <p className="px-6 pb-6 text-[#64748B] leading-relaxed">
                    {faq.resposta}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA */}
      <section className="flex flex-col items-center text-center px-6 py-24 bg-gradient-to-br from-[#1883FF] to-[#6D28D9] text-white">
        <div className="mb-6 animate-[float_4s_ease-in-out_infinite]">
          <Calendar size={48} className="text-white" />
        </div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-5 tracking-tight max-w-[800px]">
          Pronto para multiplicar seu tempo livre?
        </h2>
        <p className="text-lg text-[#E8F2FF] max-w-[600px] mb-10 leading-relaxed">
          Junte-se aos profissionais que pararam de perder horas no WhatsApp e automatizaram 100% de seus agendamentos e lembretes.
        </p>
        <Link to="/register" className="no-underline text-[#6D28D9] bg-white text-xl font-semibold px-12 py-5 rounded-xl shadow-2xl transition-all duration-300 hover:-translate-y-1">
          Criar minha conta grátis
        </Link>
      </section>

      {/* 8. FOOTER */}
      <footer className="px-6 py-14 bg-[#020617] text-white">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          
          <div className="mb-8">
            <img 
              src="/logo-sincro1.png" 
              alt="Agenda Sincro Logo" 
              className="h-12 w-auto object-contain" 
            />
          </div>

          <div className="flex gap-6 justify-center mb-8">
            <a 
              href="https://wa.me/558521807757" 
              target="_blank" 
              rel="noreferrer" 
              className="text-white no-underline flex items-center gap-2 text-sm font-bold transition-opacity hover:text-[#25d366]"
            >
              <LuMessageCircle size={20} /> 
              <span>Suporte</span>
            </a>

            <a href="https://instagram.com/agendasincro" target="_blank" rel="noreferrer" className="text-white transition-opacity hover:text-[#6D28D9]">
              <LuInstagram size={20} />
            </a>

            <a href="https://facebook.com/agendasincro" target="_blank" rel="noreferrer" className="text-white transition-opacity hover:text-[#1883FF]">
              <LuFacebook size={20} />
            </a>
          </div>

          <p className="m-0 text-sm text-[#94a3b8] font-semibold mb-2.5">
            &copy; {new Date().getFullYear()} Futurae Tecnologia e Consultoria.
          </p>
          <p className="m-0 text-xs text-[#64748b] font-bold uppercase tracking-wider mb-8">
            Feito para escalar o seu negócio.
          </p>
          
          <div className="w-full flex gap-6 flex-wrap justify-center border-t border-white/5 pt-8">
            <Link to="/politica-privacidade" className="text-xs text-[#94a3b8] no-underline transition-colors hover:text-white">Política de Privacidade</Link>
            <Link to="/termo-servico" className="text-xs text-[#94a3b8] no-underline transition-colors hover:text-white">Termos de Serviço</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;